import { useState, useEffect } from "react";
import { api, formatApiErrorDetail, formatINR, methodLabel, todayStr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "./ui/dialog";
import { RadioGroup, RadioGroupItem } from "./ui/radio-group";
import { Textarea } from "./ui/textarea";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function RecordPaymentDialog({ bill, open, onOpenChange, onDone }) {
  const remaining = bill ? bill.amount - (bill.paid_amount || 0) : 0;
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("cash");
  const [note, setNote] = useState("");
  const [billNo, setBillNo] = useState("");
  const [collectedAt, setCollectedAt] = useState(todayStr());
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (bill) {
      setAmount(String(remaining));
      setMethod("cash");
      setNote("");
      setBillNo("");
      setCollectedAt(todayStr());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bill?.id]);

  const reset = () => {
    setAmount("");
    setMethod("cash");
    setNote("");
    setBillNo("");
    setCollectedAt(todayStr());
  };

  const billNoValid = method !== "cash" || /^\d{4}$/.test(billNo);

  const submit = async () => {
    if (!billNoValid) {
      toast.error("Cash payment requires a 4-digit bill number");
      return;
    }
    setLoading(true);
    try {
      await api.post("/payments", {
        bill_id: bill.id,
        amount: Number(amount),
        method,
        online_note: note,
        bill_no: method === "cash" ? billNo : "",
        collected_at: collectedAt,
      });
      toast.success("Payment recorded");
      reset();
      onDone?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally {
      setLoading(false);
    }
  };

  if (!bill) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) reset(); onOpenChange(v); }}>
      <DialogContent className="max-w-md" data-testid="record-payment-dialog">
        <DialogHeader>
          <DialogTitle>Record Payment</DialogTitle>
          <DialogDescription>
            {bill.customer_name} · {bill.customer_code} · Remaining: <span className="font-semibold">{formatINR(remaining)}</span>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label>Amount (₹)</Label>
            <Input data-testid="payment-amount-input" type="number" min="1" value={amount} onChange={(e) => setAmount(e.target.value)} />
          </div>
          <div>
            <Label>Payment Date</Label>
            <Input data-testid="payment-date-input" type="date" max={todayStr()} value={collectedAt} onChange={(e) => setCollectedAt(e.target.value)} />
          </div>
          <div>
            <Label>Method</Label>
            <RadioGroup value={method} onValueChange={setMethod} className="grid grid-cols-3 gap-2 mt-2">
              {["cash", "gpay", "phonepe"].map((m) => (
                <label key={m} htmlFor={`m-${m}`} className={`border rounded-md px-3 py-2 text-sm cursor-pointer flex items-center gap-2 ${method === m ? "border-red-500 bg-red-50" : "border-gray-200"}`}>
                  <RadioGroupItem id={`m-${m}`} value={m} data-testid={`payment-method-${m}`} />
                  <span className="capitalize">{methodLabel(m)}</span>
                </label>
              ))}
            </RadioGroup>
          </div>
          {method === "cash" && (
            <div>
              <Label>Bill No (4-digit invoice)</Label>
              <Input
                data-testid="payment-bill-no-input"
                inputMode="numeric"
                maxLength={4}
                placeholder="e.g. 1234"
                value={billNo}
                onChange={(e) => setBillNo(e.target.value.replace(/\D/g, "").slice(0, 4))}
                className={billNo && !billNoValid ? "border-red-500" : ""}
              />
              {billNo && !billNoValid && (
                <div className="text-xs text-red-600 mt-1">Must be exactly 4 digits.</div>
              )}
            </div>
          )}
          {(method === "gpay" || method === "phonepe") && (
            <div>
              <Label>Online Note (which phone/UPI received this?)</Label>
              <Textarea data-testid="payment-note-input" rows={2} placeholder="e.g. received on 9876543210 GPay" value={note} onChange={(e) => setNote(e.target.value)} />
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => { reset(); onOpenChange(false); }}>Cancel</Button>
          <Button data-testid="payment-save-btn" disabled={loading || !amount || !billNoValid} onClick={submit} className="bg-red-600 hover:bg-red-700">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Record"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
